import {
  Botao,
  EstadoVazio,
  Modal,
  Selo,
  Tabela,
  ValorFiscal,
  type ColunaTabela,
} from '../../componentes/ui';
import { emBr } from './comum';
import { parcelasAReceber, porPagador, type ParcelaAReceber } from './filtro';
import type { Negociacao } from './tipos';

/**
 * O que está por trás do cartão "A receber": quem ainda deve, quanto, e as
 * parcelas em aberto, da que vence primeiro para a última.
 *
 * Só leitura. A baixa continua na negociação — a linha abre ela, onde o
 * pagamento é registrado com data e valor.
 */
export function ParcelasAReceber(props: {
  readonly aberto: boolean;
  readonly negociacoes: readonly Negociacao[];
  readonly aoFechar: () => void;
  readonly aoAbrirNegociacao: (id: string) => void;
}): JSX.Element {
  const parcelas = parcelasAReceber(props.negociacoes);
  const devedores = porPagador(parcelas);
  const total = parcelas.reduce((soma, p) => soma + p.parcela.valor, 0);
  const atrasadas = parcelas.filter((p) => p.atrasada);
  const totalAtrasado = atrasadas.reduce((soma, p) => soma + p.parcela.valor, 0);

  const colunasDosDevedores: readonly ColunaTabela<(typeof devedores)[number]>[] = [
    { chave: 'pagador', titulo: 'Quem paga', larguraMinima: '16rem', celula: (d) => d.pagador },
    {
      chave: 'parcelas',
      titulo: 'Parcelas',
      numerica: true,
      encolher: true,
      celula: (d) => d.parcelas,
    },
    {
      chave: 'emAtraso',
      titulo: 'Em atraso',
      numerica: true,
      larguraMinima: '9rem',
      celula: (d) =>
        d.emAtraso === 0 ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          <ValorFiscal valor={d.emAtraso} />
        ),
    },
    {
      chave: 'total',
      titulo: 'A pagar',
      numerica: true,
      larguraMinima: '9rem',
      celula: (d) => <ValorFiscal valor={d.total} variante="economia" />,
    },
  ];

  const colunasDasParcelas: readonly ColunaTabela<ParcelaAReceber>[] = [
    {
      chave: 'vencimento',
      titulo: 'Vencimento',
      larguraMinima: '7rem',
      celula: (p) => (
        <span className="flex flex-col items-start gap-1">
          <span className="valor-fiscal text-sm">{emBr(p.parcela.dataVencimento)}</span>
          {p.atrasada ? (
            <Selo tom="critico" tamanho="pequeno">
              Em atraso
            </Selo>
          ) : (
            <Selo tom="neutro" tamanho="pequeno">
              A vencer
            </Selo>
          )}
        </span>
      ),
    },
    {
      chave: 'pagador',
      titulo: 'Quem paga',
      larguraMinima: '13rem',
      celula: (p) => p.pagador,
    },
    {
      chave: 'oque',
      titulo: 'O quê',
      larguraMinima: '13rem',
      celula: (p) => (
        <span className="flex flex-col">
          <span>
            {p.honorarios ? 'Honorários' : 'Pagamento do crédito'} · parcela{' '}
            {p.parcela.numeroParcela}/{p.negociacao.parcelas}
          </span>
          {p.honorarios && (
            <span className="text-xs text-conteudo-sutil">
              para {p.negociacao.parceiroNome ?? 'o assessor'}
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'negociacao',
      titulo: 'Negociação',
      larguraMinima: '16rem',
      celula: (p) => (
        <span className="text-sm text-conteudo-secundario">
          {p.negociacao.fornecedorNome ?? 'Fornecedor'} →{' '}
          {p.negociacao.compradorNome ?? 'Comprador'}
        </span>
      ),
    },
    {
      chave: 'valor',
      titulo: 'Valor',
      numerica: true,
      larguraMinima: '9rem',
      celula: (p) => <ValorFiscal valor={p.parcela.valor} />,
    },
  ];

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="A receber"
      descricao="Quem ainda deve e as parcelas em aberto, do comprador e dos honorários. Clique numa parcela para abrir a negociação e dar baixa."
      largura="ampla"
      rodape={
        <Botao variante="secundario" onClick={props.aoFechar}>
          Fechar
        </Botao>
      }
    >
      {parcelas.length === 0 ? (
        <EstadoVazio
          titulo="Nada a receber"
          descricao="Nenhuma parcela em aberto: as negociações transferidas estão pagas, e as demais ainda não têm parcelas — elas nascem no deferimento da transferência."
        />
      ) : (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-conteudo-secundario">
            <ValorFiscal valor={total} variante="economia" /> em {parcelas.length}{' '}
            {parcelas.length === 1 ? 'parcela' : 'parcelas'}
            {atrasadas.length > 0 && (
              <>
                {' '}
                — <ValorFiscal valor={totalAtrasado} /> em atraso ({atrasadas.length}{' '}
                {atrasadas.length === 1 ? 'parcela' : 'parcelas'})
              </>
            )}
            .
          </p>

          <section aria-labelledby="titulo-devedores" className="flex flex-col gap-3">
            <h3
              id="titulo-devedores"
              className="text-xs font-semibold uppercase tracking-wide text-conteudo-sutil"
            >
              Quem ainda deve
            </h3>
            <Tabela
              legenda="Quem ainda deve, somando as parcelas em aberto"
              densidade="compacta"
              colunas={colunasDosDevedores}
              dados={devedores}
              chaveDaLinha={(d) => d.pagador}
              chaveDeCabecalhoDaLinha="pagador"
            />
          </section>

          <section aria-labelledby="titulo-parcelas" className="flex flex-col gap-3">
            <h3
              id="titulo-parcelas"
              className="text-xs font-semibold uppercase tracking-wide text-conteudo-sutil"
            >
              Parcelas em aberto
            </h3>
            <Tabela
              legenda="Parcelas em aberto, da que vence primeiro"
              densidade="compacta"
              colunas={colunasDasParcelas}
              dados={parcelas}
              chaveDaLinha={(p) => p.parcela.id}
              aoClicarNaLinha={(p) => props.aoAbrirNegociacao(p.negociacao.id)}
              linhaDestacada={(p) => p.atrasada}
            />
          </section>
        </div>
      )}
    </Modal>
  );
}
